/**
 * Filmstrip geometry for the listing lightbox.
 *
 * Pulled out of the component because jsdom has no layout — `offsetLeft` and
 * `clientWidth` are all 0 there — so the only way to test the arithmetic that
 * actually broke is to test it as arithmetic.
 *
 * What broke (measured on the deployed 86-photo listing at 1280): the strip is
 * **6,216 px wide inside a 1,280 px viewport — 21% of it reachable** — and its
 * `scrollLeft` never left 0. Pressing `End` left the active thumb at
 * `offsetLeft: 6136`, five thousand pixels off-screen, so at "86 / 86" the strip
 * still showed thumbs 1–17 with **no thumb ringed at all**. (W13 R122)
 */

export interface FilmstripMetrics {
  /** Distance from the strip's scroll origin to the active thumb's left edge. */
  thumbOffsetLeft: number;
  thumbWidth: number;
  /** Visible width of the strip. */
  clientWidth: number;
  /** Total scrollable width of the strip. */
  scrollWidth: number;
}

/**
 * Where to scroll so the active thumb sits in the middle of the strip, clamped
 * to the scrollable range. Centring naively goes negative for the first thumbs
 * and past the end for the last ones; both clamp to a real edge so the strip
 * never shows dead space beside the film.
 */
export function filmstripScrollTarget(m: FilmstripMetrics): number {
  const max = Math.max(0, m.scrollWidth - m.clientWidth);
  const centred = m.thumbOffsetLeft - m.clientWidth / 2 + m.thumbWidth / 2;
  return Math.max(0, Math.min(centred, max));
}

export interface FilmstripEdges {
  /** There is more strip to the left — fade the leading edge. */
  left: boolean;
  /** There is more strip to the right — fade the trailing edge. */
  right: boolean;
  /**
   * The whole strip fits. Only then may it be `justify-center`: a centred flex
   * container whose content overflows pushes that content off **both** ends,
   * making the leading thumbs unreachable — the same defect W12 R118 fixed in
   * the search empty state.
   */
  fits: boolean;
}

/** Tolerance for sub-pixel scroll positions and fractional layout widths. */
const EDGE_SLOP = 4;

/**
 * What to assume before the strip has been measured: **not** centred.
 *
 * This is load-bearing, and deployed verification is what proved it. With
 * `fits: true` as the initial guess, the strip's first layout is
 * `justify-center`; the effect that scrolls the active thumb into view therefore
 * measured a *centred, overflowing* container, and once measurement flipped
 * `fits` to false and the layout became `justify-start`, the scroll position was
 * computed against a layout that no longer existed. Live on the deployed
 * 86-photo listing: `End` left `scrollLeft` at **2468** where the settled layout
 * needs **4936**, so the active thumb was still off-screen — the same symptom the
 * fix was supposed to remove. Guessing "doesn't fit" is safe in both directions:
 * left-aligning a strip that *does* fit looks identical. (W13 R122b)
 */
export const INITIAL_FILMSTRIP_EDGES: FilmstripEdges = { left: false, right: false, fits: false };

export function filmstripEdges(s: {
  scrollLeft: number;
  clientWidth: number;
  scrollWidth: number;
}): FilmstripEdges {
  const max = s.scrollWidth - s.clientWidth;
  return {
    left: s.scrollLeft > EDGE_SLOP,
    right: s.scrollLeft < max - EDGE_SLOP,
    fits: max <= EDGE_SLOP,
  };
}

/**
 * Animate only the short hops. Stepping one photo at a time moves the strip a
 * thumb's width and should glide; jumping from photo 1 to photo 86 moves ~5,000
 * px and a smooth animation of that is a long, unreadable blur.
 */
export function filmstripScrollBehavior(
  currentScrollLeft: number,
  target: number,
  clientWidth: number
): ScrollBehavior {
  return Math.abs(currentScrollLeft - target) > clientWidth ? 'auto' : 'smooth';
}

/** URL param that carries which photo the full-screen gallery is showing. */
export const PHOTO_PARAM = 'photo';

/**
 * Which photo a `?photo=N` link means, as a 0-based index — or `null` for "no
 * gallery".
 *
 * `N` is 1-based in the URL because it is a human-facing number that matches the
 * "12 / 86" counter on screen. Rules:
 * - absent, empty, non-numeric, fractional, negative or `0` → `null`; the page
 *   renders normally rather than blanking or opening on a phantom index.
 * - past the end → the **last** photo. Broken-photo pruning shrinks the array
 *   after load, so a link that was valid when it was sent can outlive its
 *   target; landing on the closest real photo beats refusing to open. (W13 R123)
 */
export function photoParamIndex(search: string, photoCount: number): number | null {
  if (photoCount <= 0) return null;
  const raw = new URLSearchParams(search).get(PHOTO_PARAM);
  if (raw === null || raw.trim() === '') return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1) return null;
  return Math.min(n, photoCount) - 1;
}

/** The same URL with the gallery pointed at a photo (1-based in the URL). */
export function withPhotoParam(href: string, index: number): string {
  const url = new URL(href);
  url.searchParams.set(PHOTO_PARAM, String(index + 1));
  return url.toString();
}

/** The same URL with the gallery closed. */
export function withoutPhotoParam(href: string): string {
  const url = new URL(href);
  url.searchParams.delete(PHOTO_PARAM);
  return url.toString();
}
